import React from "react";

export interface EmptyStateCta {
  /** Button/link label text. */
  label: string;
  /** Click handler (used when href is not provided). */
  onClick?: () => void;
  /** When provided, renders an anchor tag instead of a button. */
  href?: string;
  /** CSS color value for the CTA. Defaults to the platform accent violet. */
  color?: string;
}

export interface EmptyStateProps {
  /** Emoji, icon node, or any React element displayed above the title. */
  icon: React.ReactNode;
  /** Primary message — short and direct. */
  title: string;
  /** Supporting detail text. */
  body?: string;
  /** Optional call-to-action rendered below the body. */
  cta?: EmptyStateCta;
  /** data-testid applied to the root element. Defaults to "empty-state". */
  "data-testid"?: string;
}

/**
 * EmptyState
 *
 * A centred placeholder for list/table views with no data.
 * Accepts an icon, title, optional body copy, and an optional CTA.
 * Uses inline styles so it works without any CSS framework.
 *
 * @example
 * <EmptyState
 *   icon="📭"
 *   title="No bookings yet"
 *   body="Appointments will appear here once they are created."
 *   cta={{ label: "Create booking", onClick: openModal }}
 * />
 */
export function EmptyState({
  icon,
  title,
  body,
  cta,
  "data-testid": testId = "empty-state",
}: EmptyStateProps) {
  const ctaColor = cta?.color ?? "#8b5cf6";

  const ctaElement = cta ? (
    cta.href ? (
      <a
        href={cta.href}
        data-testid="empty-state-cta"
        style={{
          display: "inline-block",
          marginTop: 4,
          padding: "8px 20px",
          background: `${ctaColor}22`,
          color: ctaColor,
          border: `1px solid ${ctaColor}44`,
          borderRadius: 8,
          fontSize: 13,
          fontWeight: 700,
          textDecoration: "none",
          cursor: "pointer",
        }}
      >
        {cta.label}
      </a>
    ) : (
      <button
        onClick={cta.onClick}
        data-testid="empty-state-cta"
        style={{
          display: "inline-block",
          marginTop: 4,
          padding: "8px 20px",
          background: `${ctaColor}22`,
          color: ctaColor,
          border: `1px solid ${ctaColor}44`,
          borderRadius: 8,
          fontSize: 13,
          fontWeight: 700,
          cursor: "pointer",
        }}
      >
        {cta.label}
      </button>
    )
  ) : null;

  return (
    <div
      data-testid={testId}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        padding: "64px 24px",
        gap: 8,
      }}
    >
      <div
        style={{
          fontSize: 44,
          lineHeight: 1,
          marginBottom: 8,
          userSelect: "none",
        }}
        aria-hidden="true"
      >
        {icon}
      </div>
      <p
        style={{
          margin: 0,
          fontSize: 16,
          fontWeight: 700,
          color: "#e5e7eb",
        }}
      >
        {title}
      </p>
      {body && (
        <p
          style={{
            margin: 0,
            fontSize: 13,
            color: "#6b7280",
            maxWidth: 360,
            lineHeight: 1.65,
          }}
        >
          {body}
        </p>
      )}
      {ctaElement}
    </div>
  );
}
